/**
 * BACK-01..BACK-19 — hook único para qualquer overlay (diálogo, folha, menu, prévia):
 * registra na pilha do `overlayManager` enquanto `open` for true, cuida do foco e expõe
 * `closeThen` para navegar a partir do overlay sem deixar entrada fantasma (BACK-07).
 *
 * Contrato: o dono controla `open`; `onRequestClose(reason)` é chamado quando o voltar, o Esc,
 * a troca de rota ou o pai pedem para fechar — o dono põe `open=false`. Se o dono mantiver o
 * overlay aberto (ex.: perguntou algo), o hook o registra de novo.
 *
 * Kill-switch `OVERLAY_STACK_V2=false`: caminho legado (push/popstate por overlay + pilha nativa).
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react'
import { OVERLAY_STACK_V2 } from '@/shared/config/featureFlags'
import {
  overlayManager,
  type OverlayCloseReason,
  type OverlayHandle,
  type OverlayManager,
  type OverlayOptions,
} from './overlayManager'
import { pushNativeOverlay } from './legacyNativeOverlayStack'
import { ensureOverlayKeyboard } from './overlayKeyboard'
import { useFocusTrap } from './useFocusTrap'

export interface UseOverlayOptions extends Omit<OverlayOptions, 'id'> {
  /** Elemento que recebe o foco ao abrir (senão, o 1º focável de `panelRef`). */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Painel do overlay: foco inicial e Tab preso. */
  panelRef?: RefObject<HTMLElement | null>
  /** Prende o Tab no painel (padrão: true quando há `panelRef`). */
  trapFocus?: boolean
  /** Devolve o foco ao gatilho ao fechar (padrão true). */
  restoreFocus?: boolean
  initialFocusDelayMs?: number
  /** Injeção para testes; padrão = singleton. */
  manager?: OverlayManager
}

export interface UseOverlayResult {
  /** Fechamento pela UI: chame junto de `setOpen(false)`; antecipa o recuo do histórico. Idempotente. */
  requestClose: (reason?: OverlayCloseReason) => void
  /** Fecha este overlay (e os de cima), consome as entradas de histórico e só então roda `cb` (navegar). */
  closeThen: (cb: () => void) => void
  isTop: boolean
}

const LEGACY_HISTORY_MARKER = { synapOverlay: true }
const LEGACY_POP_TIMEOUT_MS = 400

/** Caminho anterior à pilha única (rollback via kill-switch). */
function useLegacyOverlay(enabled: boolean, optionsRef: RefObject<UseOverlayOptions>) {
  const closingThenRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    closingThenRef.current = false
    window.history.pushState(LEGACY_HISTORY_MARKER, '')

    const handlePopState = () => {
      if (closingThenRef.current) return
      if (optionsRef.current.isDismissible?.() === false) {
        window.history.pushState(LEGACY_HISTORY_MARKER, '')
        return
      }
      optionsRef.current.onRequestClose('back')
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || optionsRef.current.closeOnEscape === false) return
      if (optionsRef.current.isDismissible?.() === false) return
      event.preventDefault()
      optionsRef.current.onRequestClose('escape')
    }
    const releaseNative = pushNativeOverlay(() => {
      if (optionsRef.current.isDismissible?.() !== false) optionsRef.current.onRequestClose('back')
      return true
    })

    window.addEventListener('popstate', handlePopState)
    window.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
      releaseNative()
      if (closingThenRef.current) return
      if (window.history.state?.synapOverlay) window.history.back()
    }
  }, [enabled, optionsRef])

  return useCallback(
    (cb: () => void) => {
      closingThenRef.current = true
      optionsRef.current.onRequestClose('ui')
      let ran = false
      const run = () => {
        if (ran) return
        ran = true
        cb()
      }
      if (!window.history.state?.synapOverlay) {
        run()
        return
      }
      window.addEventListener('popstate', run, { once: true })
      window.history.back()
      window.setTimeout(run, LEGACY_POP_TIMEOUT_MS)
    },
    [optionsRef],
  )
}

export function useOverlay(open: boolean, options: UseOverlayOptions): UseOverlayResult {
  const optionsRef = useRef(options)
  useLayoutEffect(() => {
    optionsRef.current = options
  })

  const manager = options.manager ?? overlayManager
  const id = `overlay-${useId()}`
  const handleRef = useRef<OverlayHandle | null>(null)
  /** Muda quando o gerenciador tira o overlay da pilha; se o dono não fechar, registra de novo. */
  const [registration, setRegistration] = useState(0)

  useEffect(() => {
    if (!open || !OVERLAY_STACK_V2) return
    ensureOverlayKeyboard(manager)
    const current = () => optionsRef.current
    const live: OverlayOptions = {
      id,
      get kind() {
        return current().kind
      },
      get label() {
        return current().label
      },
      get canDismiss() {
        return current().canDismiss
      },
      get isDismissible() {
        return current().isDismissible
      },
      get nonDismissibleMessage() {
        return current().nonDismissibleMessage
      },
      get closeOnEscape() {
        return current().closeOnEscape
      },
      get closeOnRouteChange() {
        return current().closeOnRouteChange
      },
      onRequestClose: (reason) => {
        if (handleRef.current === handle) handleRef.current = null
        // `ui` vem de `closeThen` (fechamento explícito): não registra de novo.
        if (reason !== 'ui') setRegistration((value) => value + 1)
        current().onRequestClose(reason)
      },
    }
    const handle = manager.register(live)
    handleRef.current = handle
    return () => {
      if (handleRef.current === handle) handleRef.current = null
      handle.release()
    }
  }, [id, manager, open, registration])

  const legacyCloseThen = useLegacyOverlay(open && !OVERLAY_STACK_V2, optionsRef)

  useFocusTrap(open, {
    panelRef: options.panelRef,
    initialFocusRef: options.initialFocusRef,
    trap: options.trapFocus ?? Boolean(options.panelRef),
    restoreFocus: options.restoreFocus,
    initialFocusDelayMs: options.initialFocusDelayMs,
  })

  const isTopInStack = useSyncExternalStore(
    manager.subscribe,
    () => manager.isTop(id),
    () => false,
  )

  const requestClose = useCallback(() => {
    handleRef.current?.release()
  }, [])

  const closeThen = useCallback(
    (cb: () => void) => {
      if (!OVERLAY_STACK_V2) {
        legacyCloseThen(cb)
        return
      }
      manager.closeThen(id, cb)
    },
    [id, legacyCloseThen, manager],
  )

  return { requestClose, closeThen, isTop: OVERLAY_STACK_V2 ? isTopInStack : open }
}
